import React, { useState, useEffect } from 'react';
import {
  Buildings, TreeStructure, Ticket, IdentificationCard, FileText, Info, WarningCircle,
  ArrowClockwise, Trash, X,
} from '@phosphor-icons/react';
import {
  getBranchNames, getGroupCode, getTickets, getSuretyName, getSuretyNameDetails,
  saveData, deleteMvoSvoSuretyDetails,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

// Normalizes both resolved and thrown responses to detect a plain-text "Success" reply.
function isSuccessResponse(res) {
  const text = typeof res === 'string'
    ? res
    : res?.error?.text ?? res?.error ?? res?.data ?? res?.text ?? '';
  return String(text).trim().toLowerCase() === 'success';
}

function SurityDetailsNotShowingForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchList, setBranchList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [ticketList, setTicketList] = useState([]);

  const [cao, setCao] = useState('');
  const [branchSchema, setBranchSchema] = useState(null);
  const [branchCode, setBranchCode] = useState(null);

  const [group, setGroup] = useState('');
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);

  const [ticket, setTicket] = useState('');
  const [ticketNo, setTicketNo] = useState(null);
  const [mvoSuretyId, setMvoSuretyId] = useState(null);

  const [subscriberName, setSubscriberName] = useState('');
  const [reason, setReason] = useState('');

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);
  // The delete arguments are kept apart from the audit entry. They used to ride
  // along as extra top-level fields ON the audit object, which meant the entry
  // had no oldData/newData at all: ChangeAuditDto has no such properties, so the
  // server dropped them and wrote the row with null olddata and null newdata —
  // a SURITY_DETAILS_DELETE record that said nothing about what was deleted.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchList(res || [])).catch(() => setBranchList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const preventExceedLength = (event, max) => {
    const input = event.target;
    if (
      input.value.length >= max &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Tab'
    ) {
      event.preventDefault();
    }
  };

  const resetFromTicket = () => {
    setTicketNo(null);
    setMvoSuretyId(null);
    setSubscriberName('');
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTicketList([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchSchema(null);
    setBranchCode(null);
    setGroup('');
    setGroupList([]);
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    resetFromBranch();
    if (!branchName) return;

    const selected = branchList.find((b) => b.branch_name === branchName);
    if (!selected) return;

    const schema = selected.branch_schema ?? selected.branch_code;
    setBranchSchema(schema);
    setBranchCode(selected.branch_code);

    try {
      const res = await getGroupCode(schema);
      setGroupList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setGroupList([]);
    }
  };

  const handleGroupChange = async (groupCodeValue) => {
    setGroup(groupCodeValue);
    resetFromGroup();
    if (!groupCodeValue) return;

    const selected = groupList.find((g) => String(g.group_Code) === String(groupCodeValue));
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(selected.chitgroup_id);

    try {
      const res = await getTickets(branchSchema, selected.group_Code);
      setTicketList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTicketList([]);
    }
  };

  const handleTicketChange = async (ticketNoValue) => {
    setTicket(ticketNoValue);
    resetFromTicket();
    if (!ticketNoValue) return;

    const selected = ticketList.find((t) => String(t.ticketno) === String(ticketNoValue));
    if (!selected) return;

    setTicketNo(selected.ticketno);

    // Call 1: display name (has valid subscriber_name)
    try {
      const res = await getSuretyName(branchSchema, branchCode, groupCode, selected.ticketno);
      const row = res && res.length ? res[0] : null;
      setSubscriberName(row?.subscriber_name || '');
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('getSuretyName failed:', err);
    }

    // Call 2: surety id (has valid tbl_trans_mvo_surety_id)
    try {
      const res = await getSuretyNameDetails(groupCode, selected.ticketno);
      const row = res && res.length ? res[0] : null;
      const suretyId = row?.tbl_trans_mvo_surety_id ?? null;
      setMvoSuretyId(suretyId);

      if (!suretyId) {
        showToast('warn', 'No Surety Details', 'No surety details found for this ticket.');
        handleClear();
      }
    } catch (err) {
      console.error('getSuretyNameDetails failed:', err);
      showToast('error', 'Load Failed', 'Unable to fetch surety details for this ticket.');
      handleClear();
    }
  };

  const reasonTrimLen = reason.trim().length;
  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const reasonInvalid = touched && (reasonTrimLen < 10 || reason.length > 200);

  const isFormValid = !!cao && !!group && !!ticket && reasonTrimLen >= 10 && reason.length <= 200;

  const confirmDelete = () => {
    setTouched(true);

    if (!isFormValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!mvoSuretyId) {
      showToast('warn', 'No Data Found', 'No surety record found for this ticket.');
      handleClear();
      return;
    }

    const trimmedReason = reason.trim();

    setPendingDelete({
      schemaName: branchSchema, groupCode, ticketNo, mvoSuretyId, chitGroupId: chitgroupId,
    });
    setPendingAudit({
      schemaName: branchSchema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'SURITY_DETAILS_DELETE',
      oldData: {
        branchSchema, branchCode, groupCode, ticketNo, chitGroupId: chitgroupId,
        mvoSuretyId, subscriberName,
      },
      newData: {
        status: 'DELETED', groupCode, ticketNo, mvoSuretyId,
      },
      reason: trimmedReason,
    });
    setPendingReason(trimmedReason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const doDelete = async () => {
    if (!pendingAudit || !pendingDelete) return;
    const audit = pendingAudit;

    setSaving(true);
    try {
      await saveData([audit]);

      try {
        const res = await deleteMvoSvoSuretyDetails(
          pendingDelete.schemaName, pendingDelete.groupCode, pendingDelete.ticketNo,
          pendingDelete.mvoSuretyId, pendingDelete.chitGroupId,
        );

        if (isSuccessResponse(res)) {
          setSaving(false);
          setShowConfirmModal(false);
          showToast('success', 'Success', 'Audit saved and surety details deleted successfully.', true);
          handleClear();
        } else {
          setSaving(false);
          setShowConfirmModal(false);
          console.error('Delete failed:', res);
          showToast('warn', 'Partial Success', `Audit saved, but delete failed: ${res}`, true);
        }
      } catch (err) {
        if (isSuccessResponse(err)) {
          setSaving(false);
          setShowConfirmModal(false);
          showToast('success', 'Success', 'Audit saved and surety details deleted successfully.', true);
          handleClear();
        } else {
          setSaving(false);
          setShowConfirmModal(false);
          console.error('Delete failed:', err);
          showToast('warn', 'Partial Success', 'Audit saved, but delete failed. Please try again.', true);
        }
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Audit save failed:', err);
      showToast('error', 'Audit Save Failed', 'Audit save failed. Delete was not performed.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="surity-not-showing-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="surity-not-showing-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IdentificationCard size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Surety Management</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Surity Details Not Showing</div>
          </div>
        </div>

        {/* SECTION A: Ticket Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load surety details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchList}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="surity-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <TreeStructure size={13} /> Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={groupList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchSchema}
              error={groupInvalid}
              testId="surity-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Ticket size={13} /> Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={ticketList}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="surity-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        {ticket && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            {/* SECTION B: Subscriber Details */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Subscriber Details</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Subscriber name fetched for the selected ticket</div>
              </div>
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                <FileText size={13} /> Subscriber Name
              </label>
              <input
                type="text"
                className="input-field"
                style={{ fontSize: '13px', background: '#F9FAFB' }}
                value={subscriberName}
                readOnly
                data-testid="surity-subscriber-name"
              />
            </div>

            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            {/* SECTION C: Reason */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for this deletion (min 10 characters)</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
              <span style={{ fontSize: '11px', color: reasonTrimLen >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
                {reason.length} / 200
              </span>
            </div>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
              rows={3}
              maxLength={200}
              placeholder="Enter reason (min 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => preventExceedLength(e, 200)}
              data-testid="surity-reason"
            />
            {reasonInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                {reasonTrimLen === 0 ? 'Reason is required.' : reasonTrimLen < 10 ? 'Minimum 10 characters required.' : 'Maximum 200 characters allowed.'}
              </div>
            )}
          </>
        )}

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="surity-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              className="btn-primary"
              style={{ background: '#DC2626', borderColor: '#DC2626' }}
              disabled={saving || !ticket}
              data-testid="surity-delete-button"
            >
              <Trash size={14} style={{ marginRight: '4px' }} /> {saving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="surity-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <WarningCircle size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                Do you want to delete this surety record? This action cannot be undone.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px', fontSize: '13px', color: '#111827' }}>
                <div><strong>Ticket No:</strong> {ticketNo}</div>
                <div><strong>Group Code:</strong> {groupCode}</div>
                <div><strong>Subscriber:</strong> {subscriberName}</div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="surity-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={saving}
                className="btn-primary"
                style={{ background: '#DC2626', borderColor: '#DC2626' }}
                data-testid="surity-confirm-delete"
              >
                <Trash size={14} style={{ marginRight: '4px' }} /> {saving ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SurityDetailsNotShowingForm;